import { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { Modal, Button as AntButton } from 'antd'
import styles from '../InternalExternalDetail.module.css'
import ProfileMetaList from './ProfileMetaList'
import ContactList from './ContactList'
import Button from './Button'
import HiredStatusHeader from './HiredStatusHeader'
import ThreeDotMenu from './ThreeDotMenu'
import PlaceBidModal from '../../QueryPage/components/organisms/PlaceBidModal'
import SelectJobModal from '../../QueryPage/components/organisms/SelectJobModal'
import { getOneUserSkillListing, postFavorites, deleteFavorites, addToMatches, retractBid, clearRetractBid } from '../../../redux/actions'
import EstimateIcon from '../../../assets/icons/EstimateIcon.svg'
import ExpIcon from '../../../assets/icons/ExpIcon.svg'
import LocIcon from '../../../assets/icons/LocIcon.svg'
import ClinicalNotesIcon from '../../../assets/icons/clinical_notes.svg'
import InvitationModal from '../InviteTalentModal/InvitationModal'

const S3_URL = process.env.REACT_APP_S3_URL || ''

const getInitials = (firstName = '', lastName = '') =>
  `${firstName?.[0] || ''}${lastName?.[0] || ''}` || '?'

const ProfileCard = ({ id }) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const routeLocation = useLocation()
  const [showBidSuccess, setShowBidSuccess] = useState(false)
  const [bidModal, setBidModal] = useState({ open: false, talent: null, talentType: null, preselectedJob: null })

  const clearReopenBidFlag = () => {
    if (routeLocation.state?.reopenPlaceBid) {
      history.replace(routeLocation.pathname + routeLocation.search, { ...routeLocation.state, reopenPlaceBid: false, reopenBidContext: null })
    }
  }

  useEffect(() => {
    if (routeLocation.state?.reopenPlaceBid && routeLocation.state?.reopenBidContext) {
      const ctx = routeLocation.state.reopenBidContext
      setBidModal({ open: true, talent: ctx.talent, talentType: ctx.talentType, preselectedJob: ctx.preselectedJob || null })
    }
  }, [routeLocation.state])
  const [matchesOpen, setMatchesOpen] = useState(false)
  const [retractModal, setRetractModal] = useState(false)
  const [editBidModal, setEditBidModal] = useState(false)
  const [showRetractSuccess, setShowRetractSuccess] = useState(false)

  const skillListings = useSelector((state) => state.oneUserskillListingReducer.skillListings)
  const authToken = useSelector((state) => state.authReducer?.user?.authToken)
  const isAuthenticated = Boolean(authToken)
  const skillListingId = skillListings?.skill_listings?.SkillListingId
  const loading = useSelector((state) => state.oneUserskillListingReducer.loading)
  const isFav = useSelector((state) => state.oneUserskillListingReducer.isFav)
  const retractLoading = useSelector((state) => state.retractBidReducers.loading)
  const retractSuccess = useSelector((state) => state.retractBidReducers.success)

  const handleJobSelect = (job) => {
    dispatch(addToMatches({
      GigId: job.JobOrderId,
      JobTitle: job.title,
      SkillListingId: skillListingId,
      SkillListingTitle: title,
    }))
    setMatchesOpen(false)
  }

  const handleHeartClick = () => {
    if (!skillListingId) return
    if (isFav) dispatch(deleteFavorites(skillListingId))
    else dispatch(postFavorites(skillListingId))
  }

  const handleRetractConfirm = () => {
    dispatch(retractBid({
      BidStatusName: 'Retracted',
      id: latestBid?.BidId,
      Message: 'Bid was retracted',
      FromMsg: true,
      SkillListingId: skillListingId,
    }))
  }

  useEffect(() => {
    if (retractSuccess) {
      setRetractModal(false)
      setShowRetractSuccess(true)
      dispatch(clearRetractBid())
      dispatch(getOneUserSkillListing({ skillId: id }))
      clearReopenBidFlag()
      const timer = setTimeout(() => setShowRetractSuccess(false), 4000)
      return () => clearTimeout(timer)
    }
  }, [retractSuccess])

  // Extract fields from /empprofile/getskilllisting/:id response
  const skillListing = skillListings?.skill_listings || {}
  const accounts = skillListing?.Profiles?.Accounts || {}
  const publicProfile = skillListings?.user_Schema?.Profiles || {}
  const firstName = accounts.FirstName || publicProfile.FirstName || ''
  const lastName = accounts.LastName || publicProfile.LastName || ''
  const name = `${firstName} ${lastName}`.trim() || 'Unknown'
  const title = skillListing.Title || ''
  const rating = parseFloat(accounts.Rating) || 0
  const reviewCount = accounts.Review || 0
  const experience = skillListings?.user_Schema?.Profiles?.ExperienceLevelName
    || skillListings?.skill_listings?.Profiles?.ExperienceLevelName
    || ''
  const MinExperience = skillListings?.user_Schema?.Profiles?.MinExperience
  const MaxExperience = skillListings?.user_Schema?.Profiles?.MaxExperience
  const experienceDisplay = MinExperience != null && MaxExperience != null
    ? (MinExperience === MaxExperience ? `${MinExperience} years` : `${MinExperience}-${MaxExperience} years`)
    : experience
  const _city = skillListing?.SkillListings_Cities?.[0]
  const location = _city?.Cities?.LocationWithCountry || _city?.LocationWithCountry || _city?.Cities?.Location || _city?.Location || ''
  const pictureUrl = accounts.PictureUrl || publicProfile.PictureUrl
  const avatarSrc = pictureUrl
    ? pictureUrl.startsWith('http')
      ? pictureUrl
      : `${S3_URL}/${pictureUrl}`
    : null
  const initials = getInitials(firstName, lastName)

  const status = skillListings?.status?.toLowerCase()
  const isHired = status === 'hired'
  const userType = skillListings?.userType || ''

  const latestBid = skillListings?.bid?.[skillListings?.bid?.length - 1]
  const hasBid = (skillListings?.bid?.length || 0) > 0

  const _gigData = latestBid?.Gig
  const _normalizedJob = _gigData ? {
    ..._gigData,
    JobOrderId: _gigData.JobOrderId,
    title: _gigData.Title || _gigData.title || '',
    location: _gigData.Location?.LocationWithCountry || _gigData.Location?.Location || '',
    JobOrderTypes: _gigData.JobOrderTypes || (_gigData.JobOrderTypeName ? { JobOrderTypeName: _gigData.JobOrderTypeName, JobOrderTypeCode: null } : null),
  } : undefined

  const editBidData = hasBid ? {
    bidId: latestBid?.BidId,
    budgetTypeId: latestBid?.BudgetTypeId,
    rate: latestBid?.Rate,
    startDate: latestBid?.IdealStartDate,
    idealTerm: latestBid?.IdealTerm,
    comments: latestBid?.Comments,
    job: _normalizedJob,
  } : null

  const messageConversationId = skillListings?.bid?.[0]?.ConversationId || null

  // Bid action visibility — use bidStatus from API if present, fall back to status-based logic
  const bidStatus = skillListings?.bidStatus || {}
  const bidActions = bidStatus.bidActions || {}
  const isBidEnabled = status === 'idle' || status === 'retract' || status === 'declined'
  const placeBidAction = bidActions.placeBid || { visible: isBidEnabled, enabled: isBidEnabled }
  const editBidAction = bidActions.editBid || { visible: hasBid && !isBidEnabled && !isHired, enabled: hasBid && !isBidEnabled && !isHired }
  const retractBidAction = bidActions.retractBid || { visible: hasBid && !isBidEnabled && !isHired, enabled: hasBid && !isBidEnabled && !isHired }

  const _userProfile = (() => { try { return JSON.parse(localStorage.getItem('userProfile')) || {} } catch { return {} } })()
  const currentUserEmail = _userProfile?.EmailAddress || ''
  const isCurrentUserHRM = _userProfile?.MasterAccountId != null
  const placedBy = bidStatus.placedBy || ''
  const bidPlacedByOther = placedBy && placedBy !== currentUserEmail
  const crossRoleBidMsg = bidPlacedByOther
    ? `Bid placed by ${isCurrentUserHRM ? 'admin' : 'HRM'} (${placedBy})`
    : null

  const linkedIn = accounts.LinkedIn || ''
  const email = accounts.EmailAddress || ''
  const phone = skillListings?.user_Schema?.Profiles?.MobilePhone || ''
  const candidateId = accounts?.AccountId || skillListings?.user_Schema?.AccountId || null
  const userProfile = JSON.parse(localStorage.getItem('userProfile'))
  const absAccountId = userProfile?.MasterAccountId || userProfile?.AccountId
  const showRemoveFromPrivate = userProfile?.EmployeeAssociation?.includes(candidateId)

  const profileStats = [
    { icon: <img src={EstimateIcon} alt="estimate" />, label: 'Estimated Amount:', value: skillListing.MinimumBid ? `$${skillListing.MinimumBid}/${skillListing.MinimumBidType || ''}` : '' },
    { icon: <img src={ExpIcon} alt="experience" />, label: 'Experience', value: experienceDisplay },
    { icon: <img src={LocIcon} alt="location" />, label: 'Location', value: location },
  ]

  const contactInfo = isAuthenticated
    ? [
        ...(linkedIn ? [{ icon: 'linkedin', text: linkedIn, href: linkedIn.startsWith('http') ? linkedIn : `https://${linkedIn}` }] : []),
        ...(email ? [{ icon: 'mail', text: email, href: `mailto:${email}` }] : []),
        ...(phone ? [{ icon: 'phone', text: phone, href: `tel:${phone}` }] : []),
      ]
    : [
        { icon: 'mail', text: 'candidate@example.com', blurred: true },
        { icon: 'phone', text: '(000) 000-0000', blurred: true },
      ]

  const talentForBid = { name, title, avatar: avatarSrc, initials }

  if (loading) {
    return (
      <aside className={styles.profileCard}>
        <div className={styles.profileBody} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
          <span>Loading...</span>
        </div>
      </aside>
    )
  }

  return (
    <aside className={styles.profileCard}>
      {isHired ? (
        <HiredStatusHeader />
      ) : (
        <div className={styles.profileHeader}>
          <span className={styles.availableTag}>Available</span>
        </div>
      )}

      <div className={styles.profileBody}>
        <div className={styles.profileTop}>
          <div className={styles.avatar}>
            {avatarSrc ? (
              <img
                src={avatarSrc}
                alt={name}
                onError={(e) => {
                  e.currentTarget.style.display = 'none'
                  const placeholder = e.currentTarget.nextSibling
                  if (placeholder) placeholder.style.display = 'flex'
                }}
              />
            ) : null}
            <div
              className={styles.avatarPlaceholder}
              style={{ display: avatarSrc ? 'none' : 'flex' }}
            >
              {initials}
            </div>
          </div>
          <div className={styles.profileInfo}>
            <div className={styles.nameRow}>
              <h2 className={styles.profileName}>{name}</h2>
              <Button
                variant="heartBtn"
                onClick={handleHeartClick}
                disabled={!isAuthenticated}
                style={isFav ? { color: 'red' } : {}}
              >
                ♥
              </Button>
            </div>
            <p className={styles.profileRole}>{title}</p>
            <div className={styles.badgeRow}>
              <span className={styles.starWrap}>★</span>
              <span className={styles.rating}>{rating.toFixed(1)} ({reviewCount})</span>
              {userType === 'PMT' ? (
                <span className={styles.pmtBadge}>PMT</span>
              ) : (
                <span className={styles.internalBadge}>Internal</span>
              )}
            </div>
          </div>
        </div>

        <ProfileMetaList items={profileStats} />
        {contactInfo.length > 0 && <ContactList items={contactInfo} />}

        <div className={styles.actionBlock}>
          {placeBidAction.visible && (
            <Button
              variant="primaryBtn"
              disabled={!placeBidAction.enabled}
              onClick={() =>
                setBidModal({
                  open: true,
                  talent: talentForBid,
                  talentType: userType === 'PMT' ? 'marketplace' : 'internal',
                })
              }
            >
              <svg width="18" height="16" viewBox="0 0 18 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                <path d="M17.4458 8.49825L1.1935 15.3303C0.907667 15.4463 0.63725 15.4234 0.38225 15.2618C0.127416 15.1001 0 14.868 0 14.5655V0.839504C0 0.537004 0.127416 0.304922 0.38225 0.143255C0.63725 -0.0184118 0.907667 -0.0432446 1.1935 0.0687554L17.4458 6.90675C17.7896 7.05609 17.9615 7.32134 17.9615 7.7025C17.9615 8.08367 17.7896 8.34892 17.4458 8.49825ZM1.6375 13.3118L15.07 7.7025L1.6375 2.01825V6.08675L7.75325 7.7025L1.6375 9.26826V13.3118Z" fill="white" />
              </svg>
              Place a Bid
            </Button>
          )}

          {(editBidAction.visible || retractBidAction.visible) && (
            <div className={styles.bidActionsRow}>
              {editBidAction.visible && (
                <Button
                  variant="secondaryBtn"
                  disabled={!editBidAction.enabled}
                  onClick={() => setEditBidModal(true)}
                >
                  Edit Bid
                </Button>
              )}
              {retractBidAction.visible && (
                <button
                  className={styles.retractBtn}
                  disabled={!retractBidAction.enabled}
                  onClick={() => setRetractModal(true)}
                >
                  Retract Bid
                </button>
              )}
            </div>
          )}

          {crossRoleBidMsg && (
            <p style={{ color: 'red', fontSize: '12px', margin: '4px 0 0', textAlign: 'center' }}>
              {crossRoleBidMsg}
            </p>
          )}

          <div className={`${styles.bottomRow} ${!isAuthenticated ? styles.bottomRowSingle : ''}`}>
            <Button
              variant="secondaryBtn"
              className={styles.resumeButton}
              onClick={() => {
                if (!isAuthenticated) {
                  history.push('/login')
                  return
                }

                history.push({
                  pathname: '/resume-3d',
                  state: { candidateid: candidateId },
                })
              }}
            >
              <img src={ClinicalNotesIcon} alt="" className={styles.resumeDoc} />
              View 3D Resume
            </Button>
            {isAuthenticated && (
              <ThreeDotMenu
                talent={talentForBid}
                skillListingId={skillListingId}
                conversationId={messageConversationId}
                showRemoveFromPrivate={showRemoveFromPrivate}
                accountId={candidateId}
                absAccountId={absAccountId}
              />
            )}
          </div>
        </div>
      </div>

      <SelectJobModal
        open={matchesOpen}
        onClose={() => setMatchesOpen(false)}
        onSelect={handleJobSelect}
      />

      <PlaceBidModal
        open={bidModal.open}
        talent={bidModal.talent}
        talentType={bidModal.talentType}
        skillListingId={skillListingId}
        preselectedJob={bidModal.preselectedJob}
        allowJobEdit
        onBidSuccess={() => {
          setShowBidSuccess(true)
          dispatch(getOneUserSkillListing({ skillId: id }))
          clearReopenBidFlag()
        }}
        onClose={() => {
          setBidModal({ open: false, talent: null, talentType: null })
          clearReopenBidFlag()
        }}
      />

      <PlaceBidModal
        open={editBidModal}
        talent={talentForBid}
        talentType={userType === 'PMT' ? 'marketplace' : 'internal'}
        skillListingId={skillListingId}
        editBidData={editBidData}
        onBidSuccess={() => {}}
        onClose={() => {
          setEditBidModal(false)
          dispatch(getOneUserSkillListing({ skillId: id }))
        }}
      />

      <Modal
        open={retractModal}
        onCancel={() => setRetractModal(false)}
        width={420}
        centered
        footer={null}
        closable={false}
        bodyStyle={{ padding: 0 }}
        style={{ borderRadius: 6, overflow: 'hidden' }}
      >
        <div className={styles.retractModalBody}>
          <button className={styles.retractModalClose} onClick={() => setRetractModal(false)}>✕</button>
          <div className={styles.retractModalIconWrap}>
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
              <path d="M10 11v6M14 11v6" />
              <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2" />
            </svg>
          </div>
          <h3 className={styles.retractModalTitle}>Retract Bid?</h3>
          <p className={styles.retractModalDesc}>
            Are you sure you want to retract your placed bid?<br />
            This action cannot be undone.
          </p>
          <div className={styles.retractModalActions}>
            <button className={styles.retractModalCancel} onClick={() => setRetractModal(false)}>
              No, go back
            </button>
            <AntButton
              danger
              type="primary"
              loading={retractLoading}
              onClick={handleRetractConfirm}
              style={{ height: 40, borderRadius: 9, fontSize: 13, fontWeight: 700 }}
            >
              Yes, retract it
            </AntButton>
          </div>
        </div>
      </Modal>

      {showBidSuccess && (
        <InvitationModal
          skipRefresh
          title="Bid Sent Successfully"
          description="Your bid has been sent successfully. You can proceed once the talent accepts it."
          ctaLabel={null}
          onClose={() => setShowBidSuccess(false)}
          onCta={() => setShowBidSuccess(false)}
        />
      )}

      {showRetractSuccess && (
        <InvitationModal
          skipRefresh
          title="Bid Retracted"
          description="Your bid has been successfully retracted."
          ctaLabel={null}
          onClose={() => setShowRetractSuccess(false)}
          onCta={() => setShowRetractSuccess(false)}
        />
      )}
    </aside>
  )
}

export default ProfileCard
